import * as Sentry from '@sentry/react';

/**
 * Errors that should ALWAYS be filtered out from Sentry.
 * These are handled gracefully by the application and don't indicate bugs.
 * They occur in normal user flows (session expiry, login pages, etc.).
 */
const ALWAYS_IGNORED_ERRORS = [
  // Expected when session expires or user isn't authenticated yet.
  // The app handles this by clearing tokens and redirecting to login.
  // Can occur on ANY route - login pages (no auth yet) or authenticated
  // pages (session expired). Always handled gracefully, never a bug.
  'No refresh token available',
];

/**
 * Errors that are only ignored on specific routes (auth/login pages).
 */
const AUTH_ROUTE_IGNORED_ERRORS = [
  // Expected when token refresh fails due to expired refresh token
  // User will be redirected to login - this is normal session expiry
  'Token refresh failed',
];

/**
 * Routes where certain authentication errors are expected (login pages).
 */
const AUTH_ROUTES = ['/login', '/preparer/login', '/auth/'];

/**
 * Check if an error should be filtered out from Sentry reporting.
 */
function shouldIgnoreError(event: Sentry.Event): boolean {
  const errorMessage = event.exception?.values?.[0]?.value || '';
  const transaction = event.transaction || '';
  const url = event.request?.url || '';

  // Always filter out errors that are handled gracefully regardless of route
  if (ALWAYS_IGNORED_ERRORS.some((ignored) => errorMessage.includes(ignored))) {
    return true;
  }

  // Check if error occurred on an auth route (login pages)
  const isOnAuthRoute = AUTH_ROUTES.some((route) => transaction.includes(route) || url.includes(route));

  // Filter out auth-route-specific errors on login pages
  if (isOnAuthRoute && AUTH_ROUTE_IGNORED_ERRORS.some((ignored) => errorMessage.includes(ignored))) {
    return true;
  }

  return false;
}

/**
 * Scrub sensitive data from the event before sending to Sentry.
 */
function scrubSensitiveData(event: Sentry.Event): void {
  // Remove authorization headers
  if (event.request?.headers) {
    delete event.request.headers['Authorization'];
    delete event.request.headers['authorization'];
    delete event.request.headers['X-Auth-Token'];
    delete event.request.headers['Cookie'];
    delete event.request.headers['cookie'];
  }

  // Scrub request body data (may contain form values, tokens, PII)
  if (event.request?.data) {
    event.request.data = '[Filtered]';
  }

  // Filter sensitive information from breadcrumbs
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((crumb) => {
      // Filter console messages that may contain sensitive data
      if (crumb.category === 'console' && crumb.message) {
        const sensitivePatterns = /token|password|auth|bearer|secret|ssn|social|ein|tax.?id/i;
        if (sensitivePatterns.test(crumb.message)) {
          return { ...crumb, message: '[Filtered - may contain sensitive data]' };
        }
      }
      return crumb;
    });
  }
}

/**
 * Initialize Sentry error monitoring and performance tracking.
 *
 * This should be called as early as possible in the application lifecycle,
 * before React renders, to capture all errors including those during initialization.
 *
 * Sentry is only enabled in production builds with a valid DSN.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  // Only initialize in production with valid DSN
  if (!import.meta.env.PROD || !dsn) {
    return;
  }

  Sentry.init({
    dsn,
    environment: 'production',

    // Release tracking for correlating errors to deployments
    release: import.meta.env.VITE_APP_VERSION || undefined,

    // Enable Sentry logging
    _experiments: {
      enableLogs: true,
    },

    integrations: [
      Sentry.browserTracingIntegration(),
      // Capture console.warn and console.error as logs in Sentry
      Sentry.consoleLoggingIntegration({ levels: ['warn', 'error'] }),
      // Session Replay - records DOM state for video-like error reproduction
      Sentry.replayIntegration(),
    ],

    // Sample 20% of transactions for performance monitoring
    tracesSampleRate: 0.2,

    // Session Replay: don't record all sessions, but capture 100% of sessions where an error occurs
    // (buffers the 60 seconds before the error + everything after)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    // CRITICAL: Enable trace propagation to backend for distributed tracing
    // This allows linking frontend requests to backend spans in Sentry
    tracePropagationTargets: ['localhost', /^https:\/\/portal-api\.ata\.net/],

    // Filter out common non-actionable errors
    ignoreErrors: [
      // Network connectivity issues - not application bugs
      'Network Error',
      'AxiosError: Network Error',
      // Browser extensions and third-party scripts
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Fetch/request cancellations (user navigated away, component unmounted)
      'AbortError',
      /^AbortError/,
      'cancelled',
      // Generic non-error rejections from third-party scripts
      'Non-Error promise rejection captured',
    ],

    // Process events before sending to Sentry
    beforeSend(event) {
      // Filter out expected errors that are handled gracefully by the app
      if (shouldIgnoreError(event)) {
        return null; // Don't send to Sentry
      }

      // Scrub sensitive data before sending
      scrubSensitiveData(event);

      return event;
    },
  });
}

// Re-export Sentry for use in components (e.g., ErrorBoundary)
export { Sentry };
